import React, { useState } from "react";
import {
  Theme,
  makeStyles,
  createStyles,
  withStyles,
  Tooltip,
  Typography,
  IconButton,
  AccordionSummary,
  Accordion,
  AccordionDetails,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Container,
  Card,
  Button,
  Box,
  CircularProgress,
} from "@material-ui/core";
import { APROVACAO, CONFIRMACAO } from "../../core/models/Enum/EventActionStep";
import { useHubx } from "../../HubX";
import useConstructor from "../../core/hooks/useConstructor";
import { BUSINESS_EVENT_UPDATED } from "../../Events";
import { FETCH_BUSINESS_EVENT_BY_ID, APPROVE_CHARGE } from "../../Fedx";
import BusinessEvent from "../../core/models/BusinessEvent";
import { useSnackbar } from "notistack";
import ChargeApprovalModal from "../../components/ChargeApprovalModal";
import { APPROVED_CHARGE, REPROVED_CHARGE } from "../../core/models/EventStatus";
import { get } from "lodash";
import { isArrayValid } from "../../helpers/utility";

const RedTextTypography = withStyles({
  root: {
    color: "#eb4034",
    fontSize: "small",
    fontWeight: "bold",
  },
})(Typography);

const buttonWrapperDiv = {
  marginRight: "10px",
} as const;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    buttonLabel: {
      lineHeight: 1.3,
    },
  })
);

type EventComponentProps = {
  // header: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  businessEvent_id: number;
  loading?: boolean;
};

const loadingState = new Array(12).fill({});

const EventFooter = ({ businessEvent_id, loading = false }: EventComponentProps) => {
  const toastr = useSnackbar();
  const hub = useHubx();
  const [businessEvent, setBusinessEvent] = useState<BusinessEvent | null>(null);
  const [currentStepNumber, setCurrentStepNumber] = useState<number>(0);
  const [displayButtonBox, setDisplayButtonBox] = useState<boolean>(true);
  const [isModalOpen, setModalOpen] = React.useState<boolean>(false);
  const [confirmationSelected, setConfirmationSelected] = React.useState<boolean>(true);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const buttonClasse = useStyles();

  const messages: string[] = [
    "Não existem ações pendentes para este fluxo",
    "Aguardando Aprovação em 1º nível",
    "Aguardando Aprovação em 2º nível",
  ];

  const getCorrectMessage = () =>
    currentStepNumber === APROVACAO
      ? messages[1]
      : currentStepNumber === CONFIRMACAO
      ? messages[2]
      : messages[0];

  useConstructor(async () => {
    const businessEvent: BusinessEvent = await hub.request(
      FETCH_BUSINESS_EVENT_BY_ID,
      businessEvent_id
    );
    setBusinessEvent(businessEvent);

    if (businessEvent && isArrayValid(businessEvent?.etapas_registradas)) {
      const etapa = businessEvent?.etapas_registradas?.reduce(
        (x, y) => (x.etapa_evento > y.etapa_evento ? x : y),
        get(businessEvent, "business_event.etapas_registradas", 0)
      )?.etapa_evento;

      if (etapa || etapa === 0) setCurrentStepNumber(etapa);
    }
  });

  const handleApproveClick = (comment: string) => {
    setIsLoading(true);
    const currentStep = {
      ...(businessEvent?.etapas_registradas.find((x) => x.etapa_evento === currentStepNumber) ??
        {}),
    };
    const currentStepWithId = {
      ...currentStep,
      event_id: businessEvent_id,
      status_cobranca: APPROVED_CHARGE,
      comentario: comment,
    };

    hub
      .request(APPROVE_CHARGE, currentStepWithId)
      .then(async (response) => {
        setIsLoading(false);
        toastr.enqueueSnackbar(`Cobrança aprovada em ${currentStepNumber - 1}º nível!`, {
          variant: "success",
        });
        const businessEvent = await hub.request(FETCH_BUSINESS_EVENT_BY_ID, businessEvent_id);
        setBusinessEvent(businessEvent);
        hub.notify(BUSINESS_EVENT_UPDATED);
        setDisplayButtonBox(false);
        setCurrentStepNumber(
          (businessEvent as BusinessEvent)?.etapas_registradas.reduce(
            (x, y) => (x.etapa_evento > y.etapa_evento ? x : y),
            get(businessEvent, "business_event.etapas_registradas", 0)
          )?.etapa_evento
        );
      })
      .catch((reject) =>
        toastr.enqueueSnackbar(`Não foi possível completar a ação, tente novamente mais tarde.`, {
          variant: "error",
        })
      );
  };

  const handleRejectClick = (comment: string) => {
    setIsLoading(true);
    const currentStep = {
      ...(businessEvent?.etapas_registradas.find((x) => x.etapa_evento === currentStepNumber) ??
        {}),
    };
    const currentStepWithId = {
      ...currentStep,
      event_id: businessEvent_id,
      status_cobranca: REPROVED_CHARGE,
      comentario: comment,
    };

    hub
      .request(APPROVE_CHARGE, currentStepWithId)
      .then(async (response) => {
        setIsLoading(false);
        toastr.enqueueSnackbar(`Cobrança rejeitada em ${currentStepNumber - 1}º nível!`, {
          variant: "warning",
        });
        const businessEvent = await hub.request(FETCH_BUSINESS_EVENT_BY_ID, businessEvent_id);
        setBusinessEvent(businessEvent);
        hub.notify(BUSINESS_EVENT_UPDATED);
        setCurrentStepNumber(
          (businessEvent as BusinessEvent)?.etapas_registradas.reduce(
            (x, y) => (x.etapa_evento > y.etapa_evento ? x : y),
            get(businessEvent, "business_event.etapas_registradas", 0)
          )?.etapa_evento
        );
        if (currentStep === CONFIRMACAO) setDisplayButtonBox(false);
      })
      .catch((reject) =>
        toastr.enqueueSnackbar(`Não foi possível completar a ação, tente novamente mais tarde.`, {
          variant: "error",
        })
      );
  };

  const buttonBox = () => {
    return (
      <Box display="flex" flexDirection="row" alignItems="center" maxHeight="25px">
        {isLoading ? (
          <div style={buttonWrapperDiv}>
            <CircularProgress />
          </div>
        ) : (
          <>
            <div style={buttonWrapperDiv}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  setConfirmationSelected(false);
                  setModalOpen(true);
                }}
                className={buttonClasse.buttonLabel}
              >
                Rejeitar
                <br />
                cobrança
              </Button>
            </div>
            <div style={buttonWrapperDiv}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setConfirmationSelected(true);
                  setModalOpen(true);
                }}
                className={buttonClasse.buttonLabel}
              >
                Aprovar
                <br />
                cobrança
              </Button>
            </div>
          </>
        )}
      </Box>
    );
  };

  return (
    <Container maxWidth={"xl"}>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        bgcolor="background.paper"
        alignItems="center"
      >
        <Box display="flex" flexDirection="column" alignItems="flex-start" p={1} m={1}>
          <b>Ação Pendente:</b>
          <RedTextTypography>{getCorrectMessage()}</RedTextTypography>
        </Box>
        {(currentStepNumber === APROVACAO || currentStepNumber === CONFIRMACAO) && displayButtonBox
          ? buttonBox()
          : null}
      </Box>
      <ChargeApprovalModal
        isOpen={isModalOpen}
        setOpen={setModalOpen}
        businessEvent_id={businessEvent_id}
        currentChargeValidationLevel={currentStepNumber === CONFIRMACAO ? 2 : 1}
        confirmationSelected={confirmationSelected}
        choiceHandlerFunction={confirmationSelected ? handleApproveClick : handleRejectClick}
      />
    </Container>
  );
};
export default EventFooter;
